import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiDialog, NoddiLink, useIsWithinTailWindBreakpoint, webButtonStyling } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { ImpersonateUserDialog, ImpersonatingBanner, useAuthContext, useBookingActions } from 'noddi-web';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import tronderdekkLogo from '../../assets/images/tronderdekk/tronderdekk-logo.png';
import { useOnImpersonated } from '../../hooks/useOnImpersonated';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { NewBookingButton } from '../Common/NewBookingButton';

export const TronderdekkHeaderNav = () => {
  const { impersonatedUser, isSuperUser, userData } = useAuthContext();
  const { clearStore } = useBookingActions();
  const onImpersonated = useOnImpersonated();
  const { currentBrand } = useCurrentBrand();
  const isLg = useIsWithinTailWindBreakpoint('lg');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalEnvironment = ['localhost', '127.0.0.1'].includes(hostname);
  const hasIamSuperuserRole = userData?.user.roles?.some((r) => r.value === 'superuser') ?? false;
  const canImpersonate = isSuperUser || hasIamSuperuserRole;

  const desktopNavLinks = [
    { label: t`Tire hotel`, href: 'https://www.tronderdekk.no/dekkhotell/' },
    { label: t`Online store`, href: 'https://www.tronderdekk.no/nettbutikk/' },
    { label: t`Rim repair`, href: 'https://www.tronderdekk.no/felgreparasjon/' },
    { label: t`Contact us`, href: 'https://www.tronderdekk.no/kontakt-oss/' }
  ];
  const mobileNavLinks = desktopNavLinks.slice(1);

  return (
    <>
      {!!impersonatedUser && (
        <ImpersonatingBanner
          onStopImpersonating={clearStore}
          onImpersonated={onImpersonated}
          defaultBrandDomain={currentBrand}
        />
      )}

      {/* Navbar */}
      <nav className='h-[90px] bg-secondary'>
        <div className='mx-auto flex h-full max-w-screen-xl items-center px-4'>
          {/* Logo */}
          {isLocalEnvironment ? (
            <Link to='/' className='shrink-0'>
              <img src={tronderdekkLogo} alt='Trønderdekk' className='max-h-[50px] w-auto lg:max-h-[70px]' />
            </Link>
          ) : (
            <a href='https://www.tronderdekk.no/' className='shrink-0'>
              <img src={tronderdekkLogo} alt='Trønderdekk' className='max-h-[50px] w-auto lg:max-h-[70px]' />
            </a>
          )}

          {/* Desktop: nav links + CTA pushed to the right */}
          {!isLg && (
            <div className='ml-auto flex items-center gap-3'>
              {canImpersonate && (
                <button
                  className={cn(
                    webButtonStyling,
                    // eslint-disable-next-line tailwindcss/no-unnecessary-arbitrary-value -- fixer emits an invalid dotted class
                    'flex items-center gap-1.5 text-base leading-[1.3] font-bold whitespace-nowrap text-white uppercase hover:text-white/80'
                  )}
                  onClick={() => setImpersonateOpen(true)}
                >
                  <NoddiIcon name='Users' color='white' size='small' />
                  Impersonate
                </button>
              )}
              {desktopNavLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={cn(
                    // eslint-disable-next-line tailwindcss/no-unnecessary-arbitrary-value -- fixer emits an invalid dotted class
                    'text-base leading-[1.3] font-bold whitespace-nowrap text-white uppercase hover:text-white/80'
                  )}
                >
                  {link.label}
                </a>
              ))}
              <NoddiLink
                to={customerRoutes.homepage.getPath()}
                variant='ghost'
                size='small'
                className='h-auto! rounded-[15px]! border border-white px-5! py-2! whitespace-nowrap text-white hover:bg-white/10'
              >
                {t`My page`}
              </NoddiLink>
              <NewBookingButton
                variant='dark'
                hideIcon
                className='h-auto! rounded-[15px]! bg-black px-5! py-2! text-white hover:bg-[#535353]'
                actionText={t`Book appointment`}
              />
            </div>
          )}

          {/* Mobile: hamburger */}
          {isLg && (
            <div className='ml-auto'>
              <button
                className={cn(
                  webButtonStyling,
                  'flex items-center gap-2 rounded-xs border border-white/50 px-3 py-1.5 text-white hover:text-white/80'
                )}
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className='hidden text-xs font-semibold uppercase sm:inline'>
                  <Trans>Menu</Trans>
                </span>
                <NoddiIcon name='Menu' color='white' size='small' />
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop that closes the mobile menu; the panel's own X button is the keyboard path */}
          <div className='fixed inset-0 z-overlay bg-black/50' onClick={() => setMobileMenuOpen(false)} />

          {/* Panel */}
          <div className='fixed top-0 right-0 z-overlay flex h-full w-[90vw] max-w-125 flex-col bg-[rgba(18,21,25,0.98)] sm:w-[65vw]'>
            {/* Close button */}
            <div className='flex justify-end px-5 py-4'>
              <button
                className={cn(webButtonStyling, 'flex items-center px-2.5 py-1 text-white hover:text-white/80')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <NoddiIcon name='X' color='white' />
              </button>
            </div>

            {/* Nav links */}
            <div className='flex flex-1 flex-col gap-6 px-5 py-4'>
              <div className='flex gap-3'>
                {/* Closes the panel: it navigates client-side, and the panel is fixed, so it
                    would otherwise stay over the destination route. */}
                <NewBookingButton
                  variant='dark'
                  defaultSize
                  hideIcon
                  className='h-11! flex-1 rounded-[15px]! bg-black px-4! text-white hover:bg-[#535353]'
                  actionText={t`Book appointment`}
                  onClick={() => setMobileMenuOpen(false)}
                />
                <NoddiLink
                  to={customerRoutes.homepage.getPath()}
                  onClick={() => setMobileMenuOpen(false)}
                  variant='ghost'
                  className='h-11! flex-1 rounded-[15px]! border border-white px-4! text-white hover:bg-white/10'
                  spanClassName='text-center'
                >
                  {t`My page`}
                </NoddiLink>
              </div>

              <div className='flex flex-col gap-4 border-t border-white/15 pt-6'>
                {mobileNavLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className='text-lg font-bold tracking-wide text-white uppercase hover:text-white/80'
                  >
                    {link.label}
                  </a>
                ))}
                {canImpersonate && (
                  <button
                    className={cn(
                      webButtonStyling,
                      'flex items-center gap-2 text-lg font-bold tracking-wide text-white uppercase hover:text-white/80'
                    )}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setImpersonateOpen(true);
                    }}
                  >
                    <NoddiIcon name='Users' color='white' size='small' />
                    Impersonate user
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <NoddiDialog
        open={impersonateOpen}
        onClose={() => setImpersonateOpen(false)}
        title='Impersonate user'
        description='Select a user from the list to view the application as them and take actions on their behalf'
        maxWidth='lg'
        className='h-[85vh] max-h-[85vh]'
        contentClassName='flex min-h-0 flex-1 flex-col overflow-hidden'
      >
        <ImpersonateUserDialog
          onClose={() => setImpersonateOpen(false)}
          onImpersonated={onImpersonated}
          defaultBrandDomain={currentBrand}
        />
      </NoddiDialog>
    </>
  );
};
